/**
 * Dashboard registry. Each widget is a self-contained component the user
 * can add / remove / drag-reorder / resize via the customizable grid.
 * Tabs scope which widgets are available where. Per-role defaults seed
 * the first visit; thereafter the layout lives in `localStorage`.
 *
 * Layout model — a flat ordered list of `{id, size}`. Tiles flow in
 * CSS-grid order; `size` picks a preset width that wraps responsively
 * across 1 / 2 / 3 / 4 columns. This swapped out an earlier x/y/w/h grid
 * model from `react-grid-layout`, which had drag-event quirks that
 * couldn't be tamed reliably across modern React versions.
 */
import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  AudioLines,
  Ban,
  BarChart3,
  CheckCircle2,
  ClipboardList,
  Coins,
  Gauge,
  Hash,
  Headphones,
  LineChart,
  ListChecks,
  Loader2,
  MicVocal,
  PhoneCall,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  Phone,
  PieChart,
  RefreshCw,
  Server,
  Sparkles,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';
import { USER_ROLES, type UserRole } from '@/lib/constants/roles';

export type TabId =
  | 'overview'
  | 'voice'
  | 'inbound'
  | 'flows'
  | 'library'
  | 'operations';

/**
 * Pseudo-tab used by the KPI strip so it shares the same customizable
 * grid mechanics (drag-reorder + size cycler) as every other tab. It
 * isn't shown in the tab bar — see `DASHBOARD_TABS`.
 */
export type LayoutScope = TabId | 'kpi';

export interface DashboardTab {
  id: TabId;
  label: string;
  icon: LucideIcon;
  roles: UserRole[];
}

const { USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN } = USER_ROLES;

export const DASHBOARD_TABS: DashboardTab[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: Gauge,
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
  },
  {
    id: 'voice',
    label: 'Voice Campaigns',
    icon: PhoneOutgoing,
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
  },
  {
    id: 'inbound',
    label: 'Inbound',
    icon: PhoneIncoming,
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
  },
  {
    id: 'flows',
    label: 'IVR & TTS',
    icon: Activity,
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
  },
  {
    id: 'library',
    label: 'Voice Library',
    icon: AudioLines,
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: Server,
    roles: [S_ADMIN, SUB_SUPER_ADMIN],
  },
];

export type WidgetId =
  | 'kpi.voice-credits'
  | 'kpi.tts-credits'
  | 'kpi.calls-today'
  | 'kpi.active-campaigns'
  | 'kpi.resellers'
  | 'kpi.end-users'
  | 'kpi.pending-prompts'
  // New dashboard-design KPI tiles
  | 'kpi.total-campaigns'
  | 'kpi.dialed'
  | 'kpi.connected'
  | 'kpi.pending'
  | 'kpi.dnd'
  | 'kpi.retry-count'
  | 'kpi.answer-rate'
  | 'kpi.dnd-rate'
  | 'kpi.retry-rate'
  | 'kpi.failed-calls'
  | 'kpi.total-users'
  | 'kpi.total-numbers'
  | 'kpi.total-calls-dialed'
  | 'kpi.total-ready'
  | 'overview.expenditure'
  | 'overview.weekly-campaigns'
  | 'overview.campaign-analysis'
  | 'overview.quick-actions'
  | 'overview.daily-traffic'
  | 'overview.outcome-donut'
  | 'overview.type-donut'
  | 'overview.top-dialed'
  | 'overview.recent-campaigns'
  | 'voice.campaign-pills'
  | 'voice.running-campaigns'
  | 'voice.reseller-running'
  | 'voice.user-drilldown'
  | 'inbound.count'
  | 'inbound.agent-groups'
  | 'flows.ivr-count'
  | 'flows.tts-flow-count'
  | 'library.recent-prompts'
  | 'library.pending-prompts'
  | 'ops.userwise-summary'
  | 'ops.call-details'
  | 'ops.pilot-cli';

/**
 * Width buckets that translate to a CSS grid column-span. Wide widgets
 * still gracefully drop to single-column on narrow viewports.
 */
export type WidgetSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export const SIZE_ORDER: WidgetSize[] = ['xs', 'sm', 'md', 'lg', 'xl'];

/**
 * Column-span per size on lg/md/sm breakpoints. The grid container uses
 * a 12-col template at lg, 6-col at md, 1-col at sm. Heights are fixed
 * per size so tiles align visually on the same row.
 */
export const SIZE_DEFS: Record<
  WidgetSize,
  { colsLg: number; colsMd: number; minHeight: number; label: string }
> = {
  // `xs` is the KPI-tile size: 2 of 12 cols at lg → 6 tiles per row, so
  // a 12-tile KPI strip lays out in exactly 2 rows. Heights are tuned to
  // a single value-line of content.
  xs: { colsLg: 2, colsMd: 3, minHeight: 76, label: 'Tiny' },
  sm: { colsLg: 3, colsMd: 3, minHeight: 110, label: 'Small' },
  md: { colsLg: 6, colsMd: 6, minHeight: 240, label: 'Medium' },
  lg: { colsLg: 8, colsMd: 6, minHeight: 320, label: 'Large' },
  xl: { colsLg: 12, colsMd: 6, minHeight: 380, label: 'Extra large' },
};

export interface WidgetDescriptor {
  id: WidgetId;
  title: string;
  description: string;
  icon: LucideIcon;
  tab: TabId | 'kpi';
  roles: UserRole[];
  defaultSize: WidgetSize;
}

export const WIDGETS: WidgetDescriptor[] = [
  /* KPI strip */
  {
    id: 'kpi.voice-credits',
    title: 'Voice credits',
    description: 'Remaining voice credits + plan.',
    icon: Wallet,
    tab: 'kpi',
    roles: [USER],
    defaultSize: 'xs',
  },
  {
    id: 'kpi.tts-credits',
    title: 'TTS credits',
    description: 'Remaining TTS credits.',
    icon: Coins,
    tab: 'kpi',
    roles: [USER],
    defaultSize: 'xs',
  },
  {
    id: 'kpi.calls-today',
    title: 'Calls today',
    description: 'Total calls placed today + success rate.',
    icon: PhoneCall,
    tab: 'kpi',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xs',
  },
  {
    id: 'kpi.active-campaigns',
    title: 'Active campaigns',
    description: 'Campaigns running right now.',
    icon: PhoneOutgoing,
    tab: 'kpi',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xs',
  },
  {
    id: 'kpi.resellers',
    title: 'Resellers',
    description: 'Total resellers on the platform.',
    icon: Users,
    tab: 'kpi',
    roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xs',
  },
  {
    id: 'kpi.end-users',
    title: 'End users',
    description: 'Total end users.',
    icon: Users,
    tab: 'kpi',
    roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xs',
  },
  {
    id: 'kpi.pending-prompts',
    title: 'Pending prompts',
    description: 'Awaiting moderation.',
    icon: ListChecks,
    tab: 'kpi',
    roles: [S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xs',
  },
  /* --- New design KPI strip (top + bottom rows in the reference design). --- */
  {
    id: 'kpi.total-campaigns',
    title: 'Total Campaigns',
    description: 'All voice campaigns on the account.',
    icon: ClipboardList,
    tab: 'kpi',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xs',
  },
  {
    id: 'kpi.dialed',
    title: 'Dialed Calls',
    description: 'Total numbers dialed across running campaigns.',
    icon: PhoneOutgoing,
    tab: 'kpi',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xs',
  },
  {
    id: 'kpi.connected',
    title: 'Connected Calls',
    description: 'Total calls connected.',
    icon: CheckCircle2,
    tab: 'kpi',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xs',
  },
  {
    id: 'kpi.pending',
    title: 'Pending Calls',
    description: 'Numbers still to be dialed.',
    icon: Loader2,
    tab: 'kpi',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xs',
  },
  {
    id: 'kpi.dnd',
    title: 'DND Numbers',
    description: 'Numbers skipped due to DND.',
    icon: Ban,
    tab: 'kpi',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xs',
  },
  {
    id: 'kpi.retry-count',
    title: 'Retry Count',
    description: 'Total retry attempts.',
    icon: RefreshCw,
    tab: 'kpi',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xs',
  },
  {
    id: 'kpi.answer-rate',
    title: 'Answer Rate',
    description: 'Connected / Dialed.',
    icon: PhoneIncoming,
    tab: 'kpi',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xs',
  },
  {
    id: 'kpi.dnd-rate',
    title: 'DND Rate',
    description: 'DND / Uploaded.',
    icon: Ban,
    tab: 'kpi',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xs',
  },
  {
    id: 'kpi.retry-rate',
    title: 'Retry Rate',
    description: 'Retry / Uploaded.',
    icon: RefreshCw,
    tab: 'kpi',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xs',
  },
  {
    id: 'kpi.failed-calls',
    title: 'Failed Calls',
    description: 'Dialed minus connected and DND.',
    icon: PhoneMissed,
    tab: 'kpi',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xs',
  },
  {
    id: 'kpi.total-users',
    title: 'Total Users',
    description: 'End users + resellers on the platform.',
    icon: Users,
    tab: 'kpi',
    roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xs',
  },
  {
    id: 'kpi.total-numbers',
    title: 'Total Numbers',
    description: 'Total numbers uploaded across all campaigns.',
    icon: ClipboardList,
    tab: 'kpi',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xs',
  },
  {
    id: 'kpi.total-calls-dialed',
    title: 'Total Calls Dialed',
    description: 'Dialed + retried calls (TCD).',
    icon: PhoneOutgoing,
    tab: 'kpi',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xs',
  },
  {
    id: 'kpi.total-ready',
    title: 'Total Ready',
    description: 'Numbers in campaigns waiting to dial (status = Ready).',
    icon: CheckCircle2,
    tab: 'kpi',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xs',
  },

  /* Overview */
  {
    id: 'overview.expenditure',
    title: 'Expenditure (7 days)',
    description: 'Daily spend across all your campaigns.',
    icon: BarChart3,
    tab: 'overview',
    roles: [USER],
    defaultSize: 'md',
  },
  {
    id: 'overview.weekly-campaigns',
    title: 'Weekly campaigns',
    description: 'Campaigns scheduled per day, last 7 days.',
    icon: BarChart3,
    tab: 'overview',
    roles: [USER],
    defaultSize: 'md',
  },
  {
    id: 'overview.campaign-analysis',
    title: 'Campaign analysis',
    description: 'Answered / DTMF / Non-answered / Spend by campaign.',
    icon: PieChart,
    tab: 'overview',
    /*
     * Legacy gates `/api/obd/campaign/analysis` to ROLE_USER + ROLE_ADMIN
     * only (Dashboard/index.js L226). The endpoint returns 403 for
     * super-admin — keep it out of their widget options.
     */
    roles: [USER, ADMIN],
    defaultSize: 'xl',
  },
  {
    id: 'overview.quick-actions',
    title: 'Quick actions',
    description: 'Jump into the common create flows.',
    icon: Sparkles,
    tab: 'overview',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'md',
  },
  {
    id: 'overview.daily-traffic',
    title: 'Daily Call Traffic',
    description: 'Calls per day over the chosen range.',
    icon: LineChart,
    tab: 'overview',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'md',
  },
  {
    id: 'overview.outcome-donut',
    title: 'Call Outcome Summary',
    description: 'Connected / Pending / DND / Failed breakdown.',
    icon: PieChart,
    tab: 'overview',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'md',
  },
  {
    id: 'overview.type-donut',
    title: 'Campaign Type Overview',
    description: 'Campaigns by template type.',
    icon: PieChart,
    tab: 'overview',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'md',
  },
  {
    id: 'overview.top-dialed',
    title: 'Top Campaigns (Dialed)',
    description: 'Top 10 campaigns ranked by dialed calls.',
    icon: TrendingUp,
    tab: 'overview',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'md',
  },
  {
    id: 'overview.recent-campaigns',
    title: 'Recent Campaigns',
    description: 'Last few campaigns at a glance.',
    icon: PhoneOutgoing,
    tab: 'overview',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'md',
  },

  /* Voice Campaigns */
  {
    id: 'voice.campaign-pills',
    title: 'Campaign totals',
    description:
      'Total numbers, dialed, pending, DnD, connected, retry, total ready.',
    icon: PieChart,
    tab: 'voice',
    roles: [S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xl',
  },
  {
    id: 'voice.running-campaigns',
    title: 'Today running campaigns',
    description: 'Live campaigns running today.',
    icon: PhoneOutgoing,
    tab: 'voice',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xl',
  },
  {
    id: 'voice.reseller-running',
    title: 'Reseller live campaigns',
    description: 'Detailed view with reports + admin extras.',
    icon: PhoneOutgoing,
    tab: 'voice',
    roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xl',
  },
  {
    id: 'voice.user-drilldown',
    title: 'User campaign drilldown',
    description:
      "Pick a user to see their running campaigns + pause/resume/stop.",
    icon: Users,
    tab: 'voice',
    roles: [S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xl',
  },

  /* Inbound */
  {
    id: 'inbound.count',
    title: 'Inbound campaigns',
    description: 'Total + running inbound campaigns, recent ones.',
    icon: PhoneIncoming,
    tab: 'inbound',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'md',
  },
  {
    id: 'inbound.agent-groups',
    title: 'Agent groups',
    description: 'Pools of agents available for patching.',
    icon: Headphones,
    tab: 'inbound',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'md',
  },

  /* IVR & TTS */
  {
    id: 'flows.ivr-count',
    title: 'Custom IVRs',
    description: 'IVR flow count + recent flows.',
    icon: Hash,
    tab: 'flows',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'md',
  },
  {
    id: 'flows.tts-flow-count',
    title: 'TTS Flows',
    description: 'TTS flow count + recent renders.',
    icon: MicVocal,
    tab: 'flows',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'md',
  },

  /* Voice Library */
  {
    id: 'library.recent-prompts',
    title: 'Recent prompts',
    description: 'Last 5 uploads with inline play.',
    icon: AudioLines,
    tab: 'library',
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'md',
  },
  {
    id: 'library.pending-prompts',
    title: 'Pending prompts',
    description: 'Approval queue.',
    icon: AudioLines,
    tab: 'library',
    roles: [S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'md',
  },

  /* Operations */
  {
    id: 'ops.userwise-summary',
    title: 'Userwise campaign summary',
    description: 'Per-user campaign totals + stats drilldown.',
    icon: Users,
    tab: 'operations',
    roles: [S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'xl',
  },
  {
    id: 'ops.call-details',
    title: 'Call details',
    description: 'Total + connected calls in a date range.',
    icon: Phone,
    tab: 'operations',
    roles: [S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'md',
  },
  {
    id: 'ops.pilot-cli',
    title: 'Pilot Wise CLI',
    description: 'CLI report with low-unarchived warnings.',
    icon: PhoneIncoming,
    tab: 'operations',
    roles: [S_ADMIN, SUB_SUPER_ADMIN],
    defaultSize: 'md',
  },
];

export interface LayoutItem {
  id: WidgetId;
  size: WidgetSize;
}

type RoleLayouts = Record<LayoutScope, LayoutItem[]>;

function ofSizes(
  ids: WidgetId[],
  overrides: Partial<Record<WidgetId, WidgetSize>> = {},
): LayoutItem[] {
  return ids
    .map((id) => {
      const desc = WIDGETS.find((w) => w.id === id);
      if (!desc) return null;
      return {
        id,
        size: overrides[id] ?? desc.defaultSize,
      };
    })
    .filter((x): x is LayoutItem => x !== null);
}

function defaultKpis(role: UserRole): LayoutItem[] {
  /*
   * Seed only the new design-row KPIs by default (Total Campaigns →
   * Retry Count). The legacy KPIs (voice credits, calls today, etc.) are
   * still in the registry, so users can re-add them from the customize
   * sheet whenever they want. New users get the design-matching layout
   * out of the box.
   */
  // First row matches the legacy "Campaign Details" pills the user
  // confirmed are correct (Total Numbers, Total Campaigns, Dialed,
  // Pending, DND, Connected, Total Calls Dialed, Retry Count, Total
  // Ready). Second row adds rate KPIs for at-a-glance health checks.
  const DESIGN_KPIS: WidgetId[] = [
    'kpi.total-numbers',
    'kpi.total-campaigns',
    'kpi.dialed',
    'kpi.pending',
    'kpi.dnd',
    'kpi.connected',
    'kpi.total-calls-dialed',
    'kpi.retry-count',
    'kpi.total-ready',
    'kpi.answer-rate',
    'kpi.dnd-rate',
    'kpi.retry-rate',
  ];
  const ids = DESIGN_KPIS.filter((id) =>
    WIDGETS.find((w) => w.id === id)?.roles.includes(role),
  );
  return ids.map((id) => ({ id, size: 'xs' as WidgetSize }));
}

function defaultsFor(role: UserRole): RoleLayouts {
  if (role === USER) {
    return {
      kpi: defaultKpis(role),
      overview: ofSizes([
        'overview.daily-traffic',
        'overview.outcome-donut',
        'overview.type-donut',
        'overview.top-dialed',
        'overview.recent-campaigns',
        'overview.quick-actions',
      ]),
      voice: ofSizes(['voice.running-campaigns']),
      inbound: ofSizes(['inbound.count', 'inbound.agent-groups']),
      flows: ofSizes(['flows.ivr-count', 'flows.tts-flow-count']),
      library: ofSizes(['library.recent-prompts']),
      operations: [],
    };
  }
  if (role === ADMIN) {
    return {
      kpi: defaultKpis(role),
      overview: ofSizes([
        'overview.daily-traffic',
        'overview.outcome-donut',
        'overview.type-donut',
        'overview.top-dialed',
        'overview.recent-campaigns',
        'overview.quick-actions',
      ]),
      voice: ofSizes([
        'voice.reseller-running',
        'voice.running-campaigns',
      ]),
      inbound: ofSizes(['inbound.count', 'inbound.agent-groups']),
      flows: ofSizes(['flows.ivr-count', 'flows.tts-flow-count']),
      library: ofSizes(['library.recent-prompts']),
      operations: [],
    };
  }
  return {
    kpi: defaultKpis(role),
    overview: ofSizes([
      'overview.daily-traffic',
      'overview.outcome-donut',
      'overview.type-donut',
      'overview.top-dialed',
      'overview.recent-campaigns',
      // campaign-analysis intentionally omitted for super-admin —
      // backend returns 403 for that role.
    ]),
    voice: ofSizes([
      'voice.campaign-pills',
      'voice.reseller-running',
      'voice.user-drilldown',
    ]),
    inbound: ofSizes(['inbound.count', 'inbound.agent-groups']),
    flows: ofSizes(['flows.ivr-count', 'flows.tts-flow-count']),
    library: ofSizes([
      'library.recent-prompts',
      'library.pending-prompts',
    ]),
    operations: ofSizes([
      'ops.userwise-summary',
      'ops.call-details',
      'ops.pilot-cli',
    ]),
  };
}

export function defaultLayoutFor(
  role: UserRole,
  scope: LayoutScope,
): LayoutItem[] {
  return defaultsFor(role)[scope] ?? [];
}

export const KPI_ORDER: WidgetId[] = [
  // Campaign volume metrics (top row in the design).
  'kpi.total-numbers',
  'kpi.total-campaigns',
  'kpi.dialed',
  'kpi.pending',
  'kpi.dnd',
  'kpi.connected',
  'kpi.total-calls-dialed',
  'kpi.retry-count',
  'kpi.total-ready',
  'kpi.failed-calls',
  // Rate metrics.
  'kpi.answer-rate',
  'kpi.dnd-rate',
  'kpi.retry-rate',
  // Legacy KPIs — all still available via the Add Widget sheet.
  'kpi.voice-credits',
  'kpi.tts-credits',
  'kpi.calls-today',
  'kpi.active-campaigns',
  'kpi.resellers',
  'kpi.end-users',
  'kpi.total-users',
  'kpi.pending-prompts',
];
